import { queryOptions } from "@tanstack/react-query";

import type { SellerThreadDetail, SellerThreadList } from "@recruiter/shared";

import { queryClient } from "../query-client";
import {
  fetchSellerProjects,
  fetchSellerThread,
  fetchSellerThreads,
  type SellerFetchOutcome,
} from "../seller-client";

// The single place talent cache entries are addressed, so the Pusher→cache
// dispatcher and the component reads reference the SAME keys (no key drift — the
// #1 cause of "the event lands but the UI doesn't update"). The directory is
// identity-free; the thread list/detail carry the sellerId so switching talent
// re-keys the read (mirrors web's `workspaceKeys.detail(projectId)`).
export const sellerKeys = {
  all: ["seller"] as const,
  projects: () => ["seller", "projects"] as const,
  threads: (sellerId: string) => ["seller", "threads", sellerId] as const,
  thread: (sellerId: string, threadId: string) => ["seller", "thread", sellerId, threadId] as const,
};

// The fetchers in seller-client.ts deliberately never throw (they return a typed
// outcome). TanStack needs a throw to drive `query.error`, so unwrap here: the
// typed `status` + the human `message` survive on SellerQueryError, which
// components render through SellerErrorState (doctrine 1.5 — surface, never swallow).
export class SellerQueryError extends Error {
  constructor(
    readonly status: 401 | 403 | 404 | "error",
    message: string,
  ) {
    super(message);
    this.name = "SellerQueryError";
  }
}

export function unwrap<T>(outcome: SellerFetchOutcome<T>): T {
  if (outcome.ok) return outcome.data;
  throw new SellerQueryError(outcome.status, outcome.message);
}

// --- queryOptions factories (TkDodo's pattern — reusable in useQuery and the
// dispatcher's cache ops; NOT a useXQuery wrapper). `staleTime: Infinity` because
// Pusher drives freshness: the queries fetch once on mount (these are navigational
// REST resources) and the dispatcher patches/invalidates thereafter. -----------

export function projectsQueryOptions() {
  return queryOptions({
    queryKey: sellerKeys.projects(),
    queryFn: async () => unwrap(await fetchSellerProjects()),
    staleTime: Infinity,
  });
}

export function threadListQueryOptions(sellerId: string) {
  return queryOptions({
    queryKey: sellerKeys.threads(sellerId),
    queryFn: async () => unwrap(await fetchSellerThreads(sellerId)),
    // Never fetch without an identity — an empty x-seller-id 401s.
    enabled: sellerId.length > 0,
    staleTime: Infinity,
  });
}

export function threadDetailQueryOptions(sellerId: string, threadId: string | null) {
  return queryOptions({
    queryKey: sellerKeys.thread(sellerId, threadId ?? ""),
    // queryFn only runs when `enabled` is true (both ids present), so the cast is safe.
    queryFn: async () => unwrap(await fetchSellerThread(threadId as string, sellerId)),
    enabled: sellerId.length > 0 && !!threadId,
    staleTime: Infinity,
  });
}

// --- single-writer cache helpers (mirror web's `updateWorkspaceCache`). Both
// NO-OP when their entry is absent: unlike web's always-present workspace, a talent
// thread detail/list has no safe empty shape, so never fabricate a partial — the
// open-on-mount useQuery fetches the authoritative copy instead. ----------------

export function updateThreadDetailCache(
  sellerId: string,
  threadId: string,
  updater: (current: SellerThreadDetail) => SellerThreadDetail,
): void {
  queryClient.setQueryData<SellerThreadDetail>(sellerKeys.thread(sellerId, threadId), (current) =>
    current ? updater(current) : current,
  );
}

export function updateThreadListCache(
  sellerId: string,
  updater: (current: SellerThreadList) => SellerThreadList,
): void {
  queryClient.setQueryData<SellerThreadList>(sellerKeys.threads(sellerId), (current) =>
    current ? updater(current) : current,
  );
}
